<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-account-group"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <div v-else>
      <div class="pa-2 mb-2">
        <autocomplete
          v-model="selectedCompany"
          :items="companies"
          :label="$t('select_company')"
          :loading="isLoadingCompanies"
          hide-details
          clearable
          @change="loadData"
        />
      </div>

      <korisnici-table :data="data" :loading="isLoading" :search-query="searchQuery" />
    </div>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import KorisniciTable from "@/components/guest/KorisniciTable.vue";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "AdminKorisniciTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    EmptyState,
    KorisniciTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      isLoading: false,
      selectedCompany: null,
      companies: [],
      isLoadingCompanies: false,
    };
  },
  computed: {
    selectedRestaurantIds() {
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map((r) => r.id).join(",");
      }
      return "";
    },
    restaurantNames() {
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map((r) => r.name).join(", ");
      }
      return "";
    },
    restaurantSummary() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      const total = this.selectedRestaurants.length;
      return `${this.$t("restaurants")} (${total})`;
    },
    restaurantNamesForFooter() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      return this.selectedRestaurants.map((r) => r.name).join(", ");
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("id"), value: "id", sortable: false },
        { text: this.$t("name_surname"), value: "nameAndEmail", sortable: false },
        {
          text: this.$t("ordered"),
          value: "order_count",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("taken"),
          value: "order_payed",
          sortable: false,
          align: "center",
        },
      ];
    },
    printHeaders() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("id"), value: "id", sortable: false },
        { text: this.$t("name_surname"), value: "nameAndSurname", sortable: false },
        { text: this.$t("email"), value: "email", sortable: false },
        {
          text: this.$t("ordered"),
          value: "order_count",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("taken"),
          value: "order_payed",
          sortable: false,
          align: "center",
        },
      ];
    },
    filteredData() {
      return this.data;
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (
        !isPrinting ||
        !this.isActiveTab ||
        !this.filteredData ||
        this.filteredData.length === 0
      ) {
        return;
      }

      this.printUsersTable();
    },
    isExcelDownloading(isDownloading) {
      if (
        !isDownloading ||
        !this.isActiveTab ||
        !this.filteredData ||
        this.filteredData.length === 0
      ) {
        return;
      }

      this.exportUsersExcel();
    },
    dateRange: {
      handler() {
        if (this.selectedRestaurantIds) {
          this.loadCompanies();
        }
      },
    },
    selectedRestaurants: {
      handler() {
        if (this.selectedRestaurantIds) {
          this.loadCompanies();
        }
      },
    },
    selectedCompany: {
      handler() {
        if (this.selectedCompany) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    async loadCompanies() {
      if (!this.selectedRestaurantIds || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoadingCompanies = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
          restaurants: this.selectedRestaurantIds,
        };

        const response = await reportService.getMultipleRestaurantGuestCompanies(params);

        // Extract keys from response object as company names
        this.companies = Object.keys(response.data || {});

        // Auto-select first company if available
        if (this.companies.length > 0 && !this.selectedCompany) {
          this.selectedCompany = this.companies[0];
          this.loadData();
        }
      } catch (error) {
        console.error("Error loading companies:", error);
        this.companies = [];
      } finally {
        this.isLoadingCompanies = false;
      }
    },
    async loadData() {
      if (
        !this.selectedRestaurantIds ||
        !this.dateRange ||
        this.dateRange.length !== 2 ||
        !this.selectedCompany
      ) {
        this.data = [];
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        const response = await reportService.getCompanyGuestKorisnici(
          this.selectedCompany,
          params
        );
        this.data = response.data?.users || [];
      } catch (error) {
        console.error("Error loading users data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    printUsersTable() {
      const tableItems = this.filteredData.map((item, index) => ({
        num: index + 1,
        id: item.id || "",
        nameAndSurname: `${item.name || ""} ${item.lastname || ""}`.trim(),
        email: item.email || "",
        order_count: item.order_count || 0,
        order_payed: item.order_payed || 0,
      }));

      const companyInfo = this.selectedCompany
        ? `<div style="text-align: center; margin-top: 8px;">${this.$t("company")}: ${
            this.selectedCompany
          }</div>`
        : "";

      this.printDocument({
        pageTitle: this.$t("users"),
        title: `${this.$t("users")}`,
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}${companyInfo}`,
        tables: [
          {
            headers: this.printHeaders.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportUsersExcel() {
      const tableItems = this.filteredData.map((item, index) => ({
        num: index + 1,
        id: item.id || "",
        nameAndSurname: `${item.name || ""} ${item.lastname || ""}`.trim(),
        email: item.email || "",
        order_count: item.order_count || 0,
        order_payed: item.order_payed || 0,
      }));

      const companyInfo = this.selectedCompany
        ? `${this.$t("company")}: ${this.selectedCompany}`
        : "";

      this.downloadExcel({
        fileName: `korisnici_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("users"),
            title: `${this.$t("users")}`,
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )} - ${companyInfo}`,
            headers: this.printHeaders.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
